import { io, Socket } from 'socket.io-client';
import { addNotification, addDocumentVerificationNotification, trackNotificationEngagement } from '@/store/slices/notificationsSlice';
import {
    addMessage,
    updateUnreadCount,
    addTypingUser,
    removeTypingUser,
    setConnectionStatus
} from '@/store/slices/messagesSlice';
import { logoutUser } from '@/store/slices/authSlice';
import { showSnackbar } from '@/store/slices/uiSlice';
import { Message } from '@/types/message';
import { Notification } from '@/types/notification';

// Enhanced socket event interfaces for verification
export interface VerificationStatusUpdate {
    documentId: string;
    status: 'pending' | 'verified' | 'rejected' | 'under_review' | 'approved' | 'in_review';
    message?: string;
    title?: string;
    rejectionReason?: string;
    trustScoreChange?: number;
    adminId?: string;
    timestamp: string;
}

export interface UploadProgressUpdate {
    documentId: string;
    progress: number;
    stage: 'uploading' | 'processing' | 'complete' | 'error';
    timestamp: string;
}

export interface VerificationNotification {
    type: 'verification_status_changed' | 'document_uploaded' | 'verification_completed';
    documentId: string;
    title: string;
    message: string;
    timestamp: string;
    status?: 'verified' | 'rejected' | 'pending' | 'under_review';
    trustScoreChange?: number;
    rejectionReason?: string;
}

// Server-to-Client Events Interface
interface ServerToClientEvents {
    // Existing message events
    notification: (notification: Notification) => void;
    'receive-message': (message: Message) => void;
    'message-read': (data: { conversationId: string; userId: string }) => void;
    'user-online': (userId: string) => void;
    'user-offline': (userId: string) => void;
    'user-typing': (data: { userId: string; conversationId: string; userName: string }) => void;
    'user-stop-typing': (data: { userId: string; conversationId: string }) => void;

    // New verification events
    verification_status_changed: (data: VerificationStatusUpdate) => void;
    upload_progress: (data: UploadProgressUpdate) => void;
    verification_notification: (data: VerificationNotification) => void;
    verification_error: (data: { message: string; code?: string }) => void;
    verification_queue_count: (data: { count: number }) => void;

    // Work submission and escrow events
    work_submitted: (data: { applicationId: string; workerName: string; gigTitle: string }) => void;
    work_reviewed: (data: { applicationId: string; decision: 'approved' | 'rejected'; employerName: string; feedback?: string }) => void;

    // Connection events
    connect: () => void;
    disconnect: () => void;
    connect_error: (error: Error) => void;
}

// Client-to-Server Events Interface
interface ClientToServerEvents {
    // Existing message events
    'send-message': (data: { room: string; message: string; conversationId: string }) => void;
    'join-room': (room: string) => void;
    'leave-room': (room: string) => void;
    'typing-start': (data: { conversationId: string }) => void;
    'typing-stop': (data: { conversationId: string }) => void;

    // New verification events
    'subscribe-verification-updates': (data: { userId: string }) => void;
    'unsubscribe-verification-updates': (data: { userId: string }) => void;
    'document-upload-progress': (data: { progress: number; documentId: string }) => void;
    work_submitted: (data: { applicationId: string; workerName: string; gigTitle: string }) => void;
    work_reviewed: (data: { applicationId: string; decision: 'approved' | 'rejected'; employerName: string; feedback?: string }) => void;
}

let dispatch: any = null;

export const setSocketDispatch = (d: any) => {
    dispatch = d;
};

class SocketService {
    private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectionDelay = 1000;
    private currentConversationId: string | null = null;
    private subscribedUserId: string | null = null;
    private verificationEventHandlers: Map<string, Function> = new Map();

    connect(token: string, userId?: string): void {
        if (this.socket?.connected) {
            return;
        }

        // Use VITE_SOCKET_URL directly, fall back to stripping api path from VITE_API_URL
        const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ||
            (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api(\/v1)?$/, '');

        console.log('Connecting to socket server:', SOCKET_URL);

        this.socket = io(SOCKET_URL, {
            auth: {
                token,
                userId
            },
            // polling first: lets the connection establish even during Render cold-starts,
            // Socket.IO then automatically upgrades to websocket once the HTTP request succeeds
            transports: ['polling', 'websocket'],
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: this.reconnectionDelay,
            reconnectionDelayMax: 30000,
            timeout: 20000,
            randomizationFactor: 0.5,
            forceNew: false
        });

        this.setupEventListeners();
    }

    disconnect(): void {
        if (this.socket) {
            // Clean up verification subscriptions
            this.unsubscribeFromVerificationUpdates();
            this.verificationEventHandlers.clear();

            this.socket.disconnect();
            this.socket = null;
        }
    }

    private setupEventListeners(): void {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            console.log('Connected to server');
            this.reconnectAttempts = 0;
            this.reconnectionDelay = 1000;

            if (dispatch) {
                dispatch(setConnectionStatus(true));
            }

            // Re-subscribe to verification updates if previously subscribed
            if (this.subscribedUserId) {
                this.subscribeToVerificationUpdates(this.subscribedUserId);
            }
        });

        this.socket.on('disconnect', (reason) => {
            console.log('Disconnected from server:', reason);
            if (dispatch) {
                dispatch(setConnectionStatus(false));
            }
        });

        this.socket.on('connect_error', (error) => {
            console.warn('Socket connection error:', error.message);

            if (error.message === 'Authentication failed' || error.message === 'Authentication token required') {
                console.error('Socket authentication failed. Token may be invalid.');
                if (dispatch) {
                    dispatch(logoutUser());
                }
            } else {
                // Only attempt reconnection if it's not an authentication issue
                // and we haven't exceeded max attempts
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.handleReconnect();
                } else {
                    console.warn('Max reconnection attempts reached, giving up');
                }
            }
        });

        // Existing message events
        this.socket.on('notification', (notification: Notification) => {
            console.log('Received notification:', notification);
            if (dispatch) {
                dispatch(addNotification(notification));

                // If it's a document verification notification, also add to specialized store
                if (['verification_update', 'document_verified', 'document_rejected'].includes(notification.type)) {
                    dispatch(addDocumentVerificationNotification(notification as any));

                    // Phase 4: Auto-track notification delivery
                    try {
                        dispatch(trackNotificationEngagement({
                            notificationId: notification._id,
                            action: 'opened'
                        }));
                    } catch (error) {
                        // Silent fail for tracking
                    }

                    // Show toast notification for immediate user feedback
                    const message = notification.message || 'Document verification status updated';
                    const toastType = notification.type === 'document_verified' ? 'success' :
                        notification.type === 'document_rejected' ? 'error' : 'info';

                    dispatch(showSnackbar({
                        message,
                        type: toastType,
                        duration: 6000
                    }));

                    // Also trigger verification status update handlers for real-time UI refresh
                    console.log('🔄 Triggering verification status update from notification...');

                    // Determine the status based on notification type
                    let status: VerificationStatusUpdate['status'] = 'pending';
                    if ((notification as any).status === 'verified') {
                        status = 'verified';
                    } else if ((notification as any).status === 'rejected') {
                        status = 'rejected';
                    } else if ((notification as any).status) {
                        status = (notification as any).status;
                    }

                    // Log the notification data structure for debugging
                    console.log('🔍 DEBUG - Full notification:', notification);

                    // Create a status update object from the notification data
                    const statusUpdate: VerificationStatusUpdate = {
                        documentId: (notification as any).documentId,
                        status: status,
                        timestamp: (notification as any).timestamp || new Date().toISOString(),
                        message: notification.message,
                        title: notification.title,
                        trustScoreChange: (notification as any).trustScoreChange,
                        rejectionReason: (notification as any).rejectionReason
                    };

                    console.log('🔄 Created status update object from notification:', statusUpdate);

                    // Call registered verification status handlers
                    let handlersCalledCount = 0;
                    this.verificationEventHandlers.forEach((handler, key) => {
                        if (key === 'verification_status_changed') {
                            console.log(`🎯 CALLING HANDLER for verification_status_changed from regular notification`);
                            handlersCalledCount++;
                            try {
                                handler(statusUpdate);
                                console.log(`✅ Handler executed successfully`);
                            } catch (error) {
                                console.error(`❌ Handler execution failed:`, error);
                            }
                        }
                    });
                    console.log(`🎯 Total handlers called from regular notification: ${handlersCalledCount}`);
                }
            }
        });

        this.socket.on('receive-message', (message: Message) => {
            console.log('Socket received message:', {
                messageId: message._id,
                conversationId: message.conversation,
                content: message.content,
                sender: message.sender,
                currentConversation: this.currentConversationId
            });

            if (dispatch) {
                // Only add the message - the addMessage reducer already handles updating conversations
                dispatch(addMessage(message));
                dispatch(updateUnreadCount(1));
            }
        });

        this.socket.on('message-read', (data: { conversationId: string; userId: string }) => {
            console.log('Message read:', data);
        });

        this.socket.on('user-online', (userId: string) => {
            console.log('User online:', userId);
        });

        this.socket.on('user-offline', (userId: string) => {
            console.log('User offline:', userId);
        });

        this.socket.on('user-typing', (data: { userId: string; conversationId: string; userName: string }) => {
            console.log('User typing:', data);
            if (data.conversationId === this.currentConversationId && dispatch) {
                dispatch(addTypingUser(data.userId));
            }
        });

        this.socket.on('user-stop-typing', (data: { userId: string; conversationId: string }) => {
            console.log('User stopped typing:', data);
            if (dispatch) {
                dispatch(removeTypingUser(data.userId));
            }
        });

        // New verification event handlers
        this.socket.on('verification_status_changed', (data: VerificationStatusUpdate) => {
            console.log('🚨 VERIFICATION STATUS CHANGED EVENT RECEIVED:', data);
            console.log('🚨 Document ID:', data.documentId);
            console.log('🚨 Status:', data.status);
            console.log('🚨 Timestamp:', data.timestamp);
            console.log('🚨 Trust Score Change:', data.trustScoreChange);
            console.log('🚨 Available handlers:', Array.from(this.verificationEventHandlers.keys()));

            // Call registered handlers
            let handlersCalledCount = 0;
            this.verificationEventHandlers.forEach((handler, key) => {
                console.log(`🎯 Checking handler for key: ${key}`);
                if (key === 'verification_status_changed') {
                    console.log(`🎯 CALLING HANDLER for verification_status_changed`);
                    handlersCalledCount++;
                    try {
                        handler(data);
                        console.log(`✅ Handler executed successfully`);
                    } catch (error) {
                        console.error(`❌ Handler execution failed:`, error);
                    }
                }
            });
            console.log(`🎯 Total handlers called: ${handlersCalledCount}`);

            // Create notification for status change
            const notification: VerificationNotification = {
                type: 'verification_status_changed',
                documentId: data.documentId,
                title: 'Document Verification Status Updated',
                message: `Your document verification status has been changed to: ${data.status}`,
                timestamp: data.timestamp
            };

            if (dispatch) {
                dispatch(addNotification({
                    _id: `verification_${data.documentId}_${Date.now()}`,
                    type: 'verification',
                    title: notification.title,
                    message: notification.message,
                    read: false,
                    createdAt: notification.timestamp
                } as Notification));
            }
        });

        this.socket.on('upload_progress', (data: UploadProgressUpdate) => {
            console.log('Upload progress:', data);

            this.verificationEventHandlers.forEach((handler, key) => {
                if (key === 'upload_progress') {
                    handler(data);
                }
            });
        });

        this.socket.on('verification_notification', (data: VerificationNotification) => {
            console.log('📢 VERIFICATION NOTIFICATION RECEIVED:', data);
            console.log('Notification Type:', data.type);
            console.log('Document ID:', data.documentId);
            console.log('Title:', data.title);
            console.log('Message:', data.message);

            // If this is a verification status change notification, treat it as a status change event
            if (data.type === 'verification_status_changed') {
                console.log('🔄 Converting notification to status change event...');
                console.log('🔄 Notification type:', data.type);

                // Determine the status based on notification data
                let status: VerificationStatusUpdate['status'] = 'pending';
                if (data.status === 'verified') {
                    status = 'verified';
                } else if (data.status === 'rejected') {
                    status = 'rejected';
                } else if (data.status) {
                    status = data.status;
                }

                // Create a status update object from the notification data
                const statusUpdate: VerificationStatusUpdate = {
                    documentId: data.documentId,
                    status: status,
                    timestamp: data.timestamp || new Date().toISOString(),
                    message: data.message,
                    title: data.title,
                    trustScoreChange: data.trustScoreChange,
                    rejectionReason: data.rejectionReason
                };

                console.log('🔄 Created status update object:', statusUpdate);

                // Call registered verification status handlers
                let handlersCalledCount = 0;
                this.verificationEventHandlers.forEach((handler, key) => {
                    console.log(`🎯 Checking handler for key: ${key}`);
                    if (key === 'verification_status_changed') {
                        console.log(`🎯 CALLING HANDLER for verification_status_changed from notification`);
                        handlersCalledCount++;
                        try {
                            handler(statusUpdate);
                            console.log(`✅ Handler executed successfully`);
                        } catch (error) {
                            console.error(`❌ Handler execution failed:`, error);
                        }
                    }
                });
                console.log(`🎯 Total handlers called from notification: ${handlersCalledCount}`);
            }

            if (dispatch) {
                dispatch(addNotification({
                    _id: `verification_notification_${data.documentId}_${Date.now()}`,
                    type: 'verification',
                    title: data.title,
                    message: data.message,
                    read: false,
                    createdAt: data.timestamp,
                    recipient: '',
                    priority: 'normal'
                } as any));
            }
        });

        this.socket.on('verification_error', (data: { message: string; code?: string }) => {
            console.error('Verification error:', data);

            this.verificationEventHandlers.forEach((handler, key) => {
                if (key === 'verification_error') {
                    handler(data);
                }
            });
        });

        this.socket.on('verification_queue_count', (data: { count: number }) => {
            console.log('Verification queue count:', data);

            this.verificationEventHandlers.forEach((handler, key) => {
                if (key === 'verification_queue_count') {
                    handler(data);
                }
            });
        });

        // Work submission and escrow event handlers
        this.socket.on('work_submitted', (data: { applicationId: string; workerName: string; gigTitle: string }) => {
            console.log('Work submitted notification:', data);

            if (dispatch) {
                dispatch(addNotification({
                    _id: `work_submitted_${data.applicationId}_${Date.now()}`,
                    type: 'application',
                    title: 'Work Submitted',
                    message: `${data.workerName} has submitted work for "${data.gigTitle}". Please review it.`,
                    read: false,
                    createdAt: new Date().toISOString(),
                    recipient: '',
                    priority: 'normal'
                } as any));

                dispatch(showSnackbar({
                    message: `${data.workerName} submitted work for "${data.gigTitle}"`,
                    type: 'info'
                }));
            }
        });

        this.socket.on('work_reviewed', (data: { applicationId: string; decision: 'approved' | 'rejected'; employerName: string; feedback?: string }) => {
            console.log('Work reviewed notification:', data);

            const decisionText = data.decision === 'approved' ? 'approved' : 'requested changes for';
            const message = data.decision === 'approved'
                ? `${data.employerName} approved your work. Payment will be released shortly.`
                : `${data.employerName} requested changes to your work.${data.feedback ? ` Feedback: ${data.feedback}` : ''}`;

            if (dispatch) {
                dispatch(addNotification({
                    _id: `work_reviewed_${data.applicationId}_${Date.now()}`,
                    type: 'application',
                    title: `Work ${data.decision === 'approved' ? 'Approved' : 'Changes Requested'}`,
                    message: message,
                    read: false,
                    createdAt: new Date().toISOString(),
                    recipient: '',
                    priority: 'normal'
                } as any));

                dispatch(showSnackbar({
                    message: `Your work has been ${decisionText}`,
                    type: data.decision === 'approved' ? 'success' : 'warning'
                }));
            }
        });
    }

    private handleReconnect(): void {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;

            setTimeout(() => {
                console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                this.socket?.connect();
            }, this.reconnectionDelay);

            // Exponential backoff with max delay of 30 seconds
            this.reconnectionDelay = Math.min(this.reconnectionDelay * 2, 30000);
        } else {
            console.error('Max reconnection attempts reached');
        }
    }

    // Existing message methods
    sendMessage(data: { room: string; message: string; conversationId: string }): void {
        this.socket?.emit('send-message', data);
    }

    joinConversation(conversationId: string): void {
        this.currentConversationId = conversationId;
        this.socket?.emit('join-room', `conversation_${conversationId}`);
    }

    leaveConversation(conversationId: string): void {
        if (this.currentConversationId === conversationId) {
            this.currentConversationId = null;
        }
        this.socket?.emit('leave-room', `conversation_${conversationId}`);
    }

    startTyping(conversationId: string): void {
        this.socket?.emit('typing-start', { conversationId });
    }

    stopTyping(conversationId: string): void {
        this.socket?.emit('typing-stop', { conversationId });
    }

    // New verification methods
    subscribeToVerificationUpdates(userId: string): void {
        if (!this.socket?.connected) {
            console.warn('Socket not connected, cannot subscribe to verification updates');
            return;
        }

        this.subscribedUserId = userId;
        console.log(`🔔 Subscribing to verification updates for user: ${userId}`);
        console.log(`🔔 Socket connected: ${this.socket.connected}`);
        console.log(`🔔 Socket ID: ${this.socket.id}`);

        this.socket.emit('subscribe-verification-updates', { userId });
        console.log(`✅ Subscription request sent for user: ${userId}`);
    }

    unsubscribeFromVerificationUpdates(): void {
        if (!this.socket?.connected || !this.subscribedUserId) {
            return;
        }

        this.socket.emit('unsubscribe-verification-updates', { userId: this.subscribedUserId });
        console.log(`Unsubscribed from verification updates for user: ${this.subscribedUserId}`);
        this.subscribedUserId = null;
    }

    emitUploadProgress(documentId: string, progress: number): void {
        if (!this.socket?.connected) {
            return;
        }

        this.socket.emit('document-upload-progress', {
            documentId,
            progress
        });
    }

    emitWorkSubmitted(data: { applicationId: string; workerName: string; gigTitle: string }): void {
        if (!this.socket?.connected) {
            return;
        }

        this.socket.emit('work_submitted', data);
    }

    emitWorkReviewed(data: { applicationId: string; decision: 'approved' | 'rejected'; employerName: string; feedback?: string }): void {
        if (!this.socket?.connected) {
            return;
        }

        this.socket.emit('work_reviewed', data);
    }



    // Event handler registration for verification events
    onVerificationStatusChanged(handler: (data: VerificationStatusUpdate) => void): void {
        console.log('🔧 Registering verification status changed handler');
        this.verificationEventHandlers.set('verification_status_changed', handler);
    }

    onUploadProgress(handler: (data: UploadProgressUpdate) => void): void {
        console.log('🔧 Registering upload progress handler');
        this.verificationEventHandlers.set('upload_progress', handler);
    }

    onVerificationError(handler: (data: { message: string; code?: string }) => void): void {
        console.log('🔧 Registering verification error handler');
        this.verificationEventHandlers.set('verification_error', handler);
    }

    onVerificationQueueCount(handler: (data: { count: number }) => void): void {
        console.log('🔧 Registering verification queue count handler');
        this.verificationEventHandlers.set('verification_queue_count', handler);
    }

    // Remove event handlers
    offVerificationStatusChanged(): void {
        console.log('🧹 Removing verification status changed handler');
        this.verificationEventHandlers.delete('verification_status_changed');
    }

    offUploadProgress(): void {
        console.log('🧹 Removing upload progress handler');
        this.verificationEventHandlers.delete('upload_progress');
    }

    offVerificationError(): void {
        console.log('🧹 Removing verification error handler');
        this.verificationEventHandlers.delete('verification_error');
    }

    offVerificationQueueCount(): void {
        console.log('🧹 Removing verification queue count handler');
        this.verificationEventHandlers.delete('verification_queue_count');
    }

    // Connection event handlers
    onConnect(handler: () => void): void {
        if (this.socket) {
            this.socket.on('connect', handler);
        }
    }

    offConnect(): void {
        if (this.socket) {
            this.socket.off('connect');
        }
    }

    onDisconnect(handler: (reason: string) => void): void {
        if (this.socket) {
            this.socket.on('disconnect', handler);
        }
    }

    offDisconnect(): void {
        if (this.socket) {
            this.socket.off('disconnect');
        }
    }

    onError(handler: (error: any) => void): void {
        if (this.socket) {
            this.socket.on('connect_error', handler);
        }
    }

    offError(): void {
        if (this.socket) {
            this.socket.off('connect_error');
        }
    }

    // Generic emit method for custom events
    emit(event: string, data: any): void {
        if (this.socket?.connected) {
            this.socket.emit(event as any, data);
        }
    }

    // Utility methods
    isConnected(): boolean {
        return this.socket?.connected || false;
    }

    getConnectionStatus(): 'connected' | 'disconnected' | 'connecting' | 'error' {
        if (!this.socket) return 'disconnected';

        if (this.socket.connected) return 'connected';
        if (this.socket.disconnected) return 'disconnected';
        // Check if socket is actively trying to connect
        if ((this.socket as any).io?.engine?.readyState === 'opening') return 'connecting';

        return 'error';
    }
}

export const socketService = new SocketService();